// Builds a short, human "why this is recommended" explanation for a game shown
// in the "More Games Like This" section, by comparing the game the user is
// viewing (source) with each recommended game. Everything is derived from data
// already on the game records (genres / tags / game modes), so the explanation
// is truthful and needs no extra API call.

// Fields like genres/tags/game_modes can arrive as an array of objects, an
// array of strings, or a JSON string (sometimes double-encoded). Normalise them
// all to a clean array of display names.
export function parseEntityNames(field) {
  if (!field) return [];

  let value = field;
  // Unwrap JSON strings (handle double-encoding defensively).
  for (let i = 0; i < 3 && typeof value === "string"; i++) {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      value = JSON.parse(trimmed);
    } catch (e) {
      // A bare string that isn't JSON — treat as a single name.
      return [trimmed];
    }
  }

  if (!Array.isArray(value)) {
    if (value && typeof value === "object" && value.name) return [String(value.name)];
    return [];
  }

  return value
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") return item.name || item.title || "";
      return "";
    })
    .map((s) => String(s).trim())
    .filter(Boolean);
}

// Tags that don't describe gameplay and would only add noise to a card.
const NOISE_TAG = /(available|enabled|anti-cheat|steam|achiev|cloud|trading|workshop|remote play|captions|controller)/i;

const cleanTags = (names) =>
  names.filter((n) => n && n.length <= 22 && !NOISE_TAG.test(n));

const dedupeByLower = (names) => {
  const seen = new Set();
  const out = [];
  for (const n of names) {
    const k = n.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(n);
    }
  }
  return out;
};

const sharedByLower = (a, b) => {
  const bl = new Set(b.map((x) => x.toLowerCase()));
  return dedupeByLower(a.filter((x) => bl.has(x.toLowerCase())));
};

/**
 * @returns {{ reason: string, tags: string[] }}
 *   reason – a short match explanation
 *   tags   – up to 3 key gameplay tags to show on the card (shared ones first)
 */
export function getMatchInfo(sourceGame, recGame, isWildcard = false) {
  const recTags = cleanTags(dedupeByLower(parseEntityNames(recGame?.tags)));
  const recGenres = dedupeByLower(parseEntityNames(recGame?.genres));
  const recModes = dedupeByLower(parseEntityNames(recGame?.game_modes));

  if (isWildcard) {
    return {
      reason: "Wildcard pick — something a little different",
      tags: [...recGenres.slice(0, 1), ...recTags].slice(0, 3),
    };
  }

  const srcTags = cleanTags(dedupeByLower(parseEntityNames(sourceGame?.tags)));
  const srcGenres = dedupeByLower(parseEntityNames(sourceGame?.genres));
  const srcModes = dedupeByLower(parseEntityNames(sourceGame?.game_modes));

  const sharedTags = sharedByLower(recTags, srcTags);
  const sharedGenres = sharedByLower(recGenres, srcGenres);
  const sharedModes = sharedByLower(recModes, srcModes);

  let reason;
  if (sharedTags.length >= 2) {
    reason = `Shared ${sharedTags[0]} and ${sharedTags[1]}`;
  } else if (sharedTags.length === 1 && sharedGenres.length >= 1) {
    reason = `Similar ${sharedGenres[0]}, both ${sharedTags[0]}`;
  } else if (sharedTags.length === 1) {
    reason = `Shares its ${sharedTags[0]} feel`;
  } else if (sharedGenres.length >= 2) {
    reason = `Both blend ${sharedGenres[0]} and ${sharedGenres[1]}`;
  } else if (sharedGenres.length === 1 && sharedModes.length >= 1) {
    reason = `${sharedGenres[0]} with ${sharedModes[0].toLowerCase()} play`;
  } else if (sharedGenres.length === 1) {
    reason = `Another ${sharedGenres[0]} game to try`;
  } else if (sharedModes.length >= 1) {
    reason = `Both offer ${sharedModes[0].toLowerCase()} play`;
  } else {
    // Fall back to the recommendation profile, then a safe default.
    const primary = recGame?.recommendation_profile?.summary?.primary_gameplay;
    reason = primary ? `Similar vibe: ${primary}` : "Picked to match this game";
  }

  // Key tags for the card: shared ones first (most persuasive), then fill.
  const tags = dedupeByLower([
    ...sharedTags,
    ...sharedGenres,
    ...recTags,
    ...recGenres,
  ]).slice(0, 3);

  return { reason, tags };
}
